import {
    useQuery,
    useInfiniteQuery,
    keepPreviousData
} from '@tanstack/react-query'
import axios from 'axios'
import { useAuth } from './useAuth'

interface IRequestMeta {
    pagination: { page: number, pageSize: number, pageCount: number, total: number }
}

interface IIcon {
    id: number
    documentId: string
    url: string
}

export interface IExerciseRecords {
    count: number
    createdAt: string
    date: string
    deviceId: string
    documentId: string
    durationInSecond: number
    exercise: string
    icon?: IIcon
    id: number
    mode: string
    position: number
    publishedAt: string
    school: string
    updatedAt: string
    grade?: number
    className?: string
    studentNumber?: number
}

interface IExerciseRecordsResponse {
    meta: IRequestMeta
    data: IExerciseRecords[]
}

// Devices whose records were uploaded without a school name
const EXTRA_DEVICE_IDS_BY_SCHOOL: Record<string, string[]> = {
    cys: ['c397b386-f2fb-4f15-b4ca-6cca3eff0123'],
}

export interface IDateRange {
    startDate: Date
    endDate: Date
}

export const getLast12MonthsRange = (): IDateRange => {
    const today = new Date()
    return {
        startDate: new Date(today.getFullYear(), today.getMonth() - 12, today.getDate() - 1),
        endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
    }
}

const getExerciseRecords = async ({ pageParam, school = '', range = getLast12MonthsRange() }: { pageParam: number, school?: string, range?: IDateRange }) => {
    const { startDate, endDate } = range
    const extraDeviceIds = EXTRA_DEVICE_IDS_BY_SCHOOL[school.toLowerCase()] ?? []
    const result = await axios.get<IExerciseRecordsResponse>('/api/exercise-records', {
        params: {
            'pagination[page]': pageParam,
            'pagination[pageSize]': 30,
            filters: {
                $or: [
                    { school: { $contains: school } },
                    ...extraDeviceIds.map(deviceId => ({ deviceId: { $eq: deviceId } }))
                ],
                date: {
                    $between: [startDate.toISOString(), endDate.toISOString()]
                }
            },
            sort: ['date:desc'],
            populate: {
                icon: {
                    fields: 'url'
                }
            }
        }
    })
    return result.data
}

export const useExerciseRecords = (range?: IDateRange) => {
    const { me: { data, isSuccess: isMeLoaded } } = useAuth()

    const query = useInfiniteQuery({
        queryKey: ['exercise-records', data?.school_info.slug, 'paged', range?.startDate.toISOString(), range?.endDate.toISOString()],
        queryFn: async ({ pageParam }) => {
            return getExerciseRecords({ pageParam, school: data?.school_info.slug, range })
        },
        placeholderData: keepPreviousData,
        initialPageParam: 0,
        enabled: isMeLoaded,
        getNextPageParam: (lastPage) => {
            const { page, pageCount } = lastPage.meta.pagination
            return page < pageCount ? page + 1 : undefined
        },
    })

    const queryLast12Months = useQuery({
        queryKey: ['exercise-records', data?.school_info.slug],
        queryFn: async () => {
            let allData: IExerciseRecords[] = []
            let currentPage = 1
            let totalPages = 1
            while (currentPage <= totalPages) {
                const result = await getExerciseRecords({ pageParam: currentPage, school: data?.school_info.slug })
                allData = allData.concat(result.data)
                totalPages = result.meta.pagination.pageCount
                currentPage++
            }
            return allData
        },
        enabled: isMeLoaded,
    })

    return { query, queryLast12Months }
}